import React from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { ButtonQHDS, LoadingSpinnerQldDS } from '@components';
import { Cancel, Document, DocumentError, Retry, Success, Trash, WarningQld } from '@public/icons';
import {
	FileUploadStatus,
	FileUploadStatusResponse,
	FileUploadValidationErrorMessage,
	UpdateAction,
} from '@types';
import { FileUploadItem, formatBytes } from '@utils';

// Styles
import styles from './uploadedFileList.module.scss';

/**
 * Props for the UploadedFileList component.
 */
interface UploadedFileListProps {
	/**
	 * Array of files to be displayed in the list.
	 */
	files: FileUploadItem[];
	/**
	 * Boolean indicating whether the component is disabled.
	 */
	disabled: boolean;
	/**
	 * Function to remove a file from the list, given its index.
	 */
	handleRemove: (index: number) => void;
	/**
	 * Function to restore a file from the list, given its index.
	 */
	handleRestore: (file: FileUploadItem) => void;
	/**
	 * Function to handle file upload
	 * @param file - The file to be uploaded
	 * @param setUploadingFiles - Function to update the state of uploading files
	 * @returns A promise that resolves when the upload is complete
	 */
	uploadFileMutation: UseMutationResult<FileUploadStatusResponse, unknown, File, unknown>;
	/**
	 * Function to update the status of a file
	 * @param fileName - The name of the file
	 * @param status - The new status of the file
	 */
	updateFileUploadStatus: (fileName: string, status: FileUploadStatusResponse) => void;
}

export const UploadedFileList: React.FC<UploadedFileListProps> = ({
	files,
	disabled,
	handleRemove,
	handleRestore,
	uploadFileMutation,
	updateFileUploadStatus,
}) => {
	const handleRetry = async (file: FileUploadItem) => {
		if (file.file) {
			updateFileUploadStatus(file.file.name, {
				status: FileUploadStatus.REQUESTED,
				fileuri: null,
				contactid: null,
			});
			uploadFileMutation.mutate(file.file);
		}
	};

	return (
		<div className={styles['file-list']} role="list" aria-label="Uploaded files">
			{files.map((file, index) => {
				const fileName = file.file?.name ?? file.filename ?? '';
				const uploadInProgress = file.uploadStatus?.status === FileUploadStatus.REQUESTED;
				const uploadedSuccessful = file.uploadStatus?.status === FileUploadStatus.CLEAN;
				const fileSize = file.file ? ` - ${formatBytes(file.file.size)}` : '';
				const toBeRemoved = file.action === UpdateAction.REMOVE;
				const restored = file.uploadStatus?.status === FileUploadStatus.RESTORED;
				const isMalwareError = file.uploadStatus?.status === FileUploadStatus.DIRTY;

				const hasError = !!file.error || file.uploadStatus?.status === FileUploadStatus.FAILED;
				const isApiError =
					hasError &&
					!isMalwareError &&
					!Object.values(FileUploadValidationErrorMessage).includes(
						file.error as FileUploadValidationErrorMessage,
					);

				const renderFileStatus = (file: FileUploadItem) => {
					if (hasError) {
						return (
							<>
								<WarningQld customStyles={styles['warning-icon']} aria-hidden="true" />
								<p className={styles['file-error']} title={`${file.error}${fileSize}`}>
									{file.error === FileUploadValidationErrorMessage.INVALID_FILE_FORMAT
										? `${file.error} - ${file.file?.name.split('.').pop()}`
										: `${file.error}${fileSize}`}
								</p>
							</>
						);
					} else if (toBeRemoved) {
						return <p className={styles['file-error']}>Marked for removal</p>;
					} else if (uploadInProgress) {
						return <p className={styles['file-size']}>Uploading...</p>;
					} else if (uploadedSuccessful || restored) {
						return (
							<>
								<Success customStyles={styles['success-icon']} aria-hidden="true" />
								<p className={styles['file-size']} title={`Upload successful${fileSize}`}>
									Upload successful{fileSize}
								</p>
							</>
						);
					} else {
						return null;
					}
				};

				return (
					<div
						key={index}
						className={[
							styles['file-item'],
							(hasError || toBeRemoved) && styles['file-item-error'],
						].join(' ')}
						role="listitem"
						aria-labelledby={`file-name-${index}`}
						aria-describedby={`file-status-${index}`}
					>
						<div className={styles['file-info']}>
							{uploadInProgress ? (
								<div>
									<LoadingSpinnerQldDS aria-hidden="true" />
								</div>
							) : hasError || toBeRemoved ? (
								<DocumentError aria-hidden="true" customStyles={styles['file-icon']} />
							) : (
								<Document aria-hidden="true" customStyles={styles['file-icon']} />
							)}
							<div className={styles['file-details']}>
								<p id={`file-name-${index}`} className={styles['file-name']} title={fileName}>
									{fileName}
								</p>
								<div id={`file-status-${index}`} className={styles['file-status']}>
									{renderFileStatus(file)}
								</div>
							</div>
						</div>

						<div className={styles['button-container']}>
							{isApiError && (
								<ButtonQHDS onClick={() => handleRemove(index)}>
									<Trash customStyles={styles['remove-icon']} aria-hidden="true" />
								</ButtonQHDS>
							)}
							<ButtonQHDS
								size="small"
								type="tertiary"
								disabled={disabled}
								onClick={() => {
									if (isApiError) {
										handleRetry(file);
									} else if (toBeRemoved) {
										handleRestore(file);
									} else {
										handleRemove(index);
									}
								}}
								aria-label={
									uploadInProgress
										? `Cancel upload of ${file.file?.name}`
										: isApiError
											? `Retry upload of ${file.file?.name}`
											: `Remove ${file.file?.name}`
								}
							>
								<div className={styles['button-content']}>
									{uploadInProgress ? (
										<>
											<Cancel customStyles={styles['remove-icon']} aria-hidden="true" />
											Cancel
										</>
									) : isApiError ? (
										<>
											<Retry customStyles={styles['warning-icon']} aria-hidden="true" />
											Retry
										</>
									) : toBeRemoved ? (
										<>
											<Retry customStyles={styles['remove-icon']} aria-hidden="true" />
											Restore
										</>
									) : (
										<>
											<Trash customStyles={styles['remove-icon']} aria-hidden="true" />
											Remove
										</>
									)}
								</div>
							</ButtonQHDS>
						</div>
					</div>
				);
			})}
		</div>
	);
};
